import { useCallback, useState, useEffect } from 'react';
import { useNavigate, NavigateOptions } from 'react-router-dom';

import { isSpecialPath } from '@app/utils/route_path';

import { addLanguagePrefix } from '../utils/language-url';
import i18n from '../config';

/**
 * Хук для навигации с автоматическим добавлением языкового префикса
 *
 * @example
 * const navigate = useLocalizedNavigate();
 * navigate('/features/'); // → /ru/features/ (если текущий язык ru)
 * navigate('/s/abc123', { skipLocalization: true }); // → /s/abc123 (без префикса)
 */
export const useLocalizedNavigate = () => {
  const navigate = useNavigate();
  // Отслеживаем изменения языка для реактивности
  const [, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return useCallback(
    (to: string, options?: NavigateOptions & { skipLocalization?: boolean }) => {
      const { skipLocalization, ...navigateOptions } = options || {};

      // Если skipLocalization = true, используем оригинальный путь
      if (skipLocalization) {
        navigate(to, navigateOptions);
        return;
      }

      // Проверяем, является ли ссылка внешней
      const isExternalLink =
        to.startsWith('http://') ||
        to.startsWith('https://') ||
        to.startsWith('mailto:') ||
        to.startsWith('tel:');

      // Если внешняя ссылка или специальный путь, не добавляем префикс
      if (isExternalLink || isSpecialPath(to)) {
        navigate(to, navigateOptions);
        return;
      }

      // Получаем актуальный язык при каждом вызове
      const language = i18n.language;
      const localizedPath = addLanguagePrefix(to, language);
      navigate(localizedPath, navigateOptions);
    },
    [navigate],
  );
};
